import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import history from '../history';

// Guard against duplicate listeners if addDeepLinkListeners is called more than once.
let _deepLinkListenersAdded = false;

/**
 * Auth0 login/logout callbacks are handled separately in services/auth.js and
 * always arrive on the custom scheme (us.foodrescue.foodrescueus://…). Universal
 * Links / App Links arrive on our https web domain. Only the latter are deep
 * links we route here — this keeps the two appUrlOpen consumers from entangling.
 */
const isWebDeepLink = (url) => !!url && /^https?:\/\//i.test(url);

/**
 * Turn a full deep-link URL — e.g. https://app.foodrescue.us/signup?invitation_hash=…
 * or https://app.foodrescue.us/verify-email?token=… — into a client-side
 * navigation, preserving path, query and hash. The origin is stripped so it
 * works regardless of environment host.
 */
const navigateFromUrl = (url) => {
  if (!isWebDeepLink(url)) {
    return;
  }

  try {
    const { pathname, search, hash } = new URL(url);
    history.push(`${pathname}${search}${hash}`);
  } catch (err) {
    console.error('Failed to handle deep link', url, err);
  }
};

/**
 * Register the Universal Link / App Link handler for native platforms.
 * Call once at app startup (src/index.js).
 */
export const addDeepLinkListeners = async () => {
  if (!Capacitor.isNativePlatform() || _deepLinkListenersAdded) {
    return;
  }

  _deepLinkListenersAdded = true;

  CapacitorApp.addListener('appUrlOpen', ({ url }) => navigateFromUrl(url));

  // Cold start: the app may have been launched by the link before the listener
  // attached, so the appUrlOpen event can be missed. Replay the launch URL.
  // getLaunchUrl returns nothing for a normal icon launch, so this is a no-op
  // outside of link-driven launches. A duplicate navigation to the same route
  // (if appUrlOpen also fires) is harmless.
  try {
    const launch = await CapacitorApp.getLaunchUrl();

    if (launch?.url) {
      navigateFromUrl(launch.url);
    }
  } catch (err) {
    console.error('Failed to read launch URL', err);
  }
};
